import type * as Phaser from 'phaser';
import type { Lane, SpawnPattern, PatternEntity, Difficulty } from '@/types';
import { PATTERNS } from '@/config/patterns';
import type { SeededRNG } from '@/utils/rng';
import { weightedEntries, type NutrientKey } from '@/config/nutrients';
import type { Nutrient } from '@/entities/Nutrient';
import type { Monster } from '@/entities/Monster';

export interface SpawnFactories {
  acquireNutrient: () => Nutrient;
  acquireMonster: () => Monster;
}

export function poolForPhase(
  patterns: readonly SpawnPattern[],
  difficulties: readonly Difficulty[],
): readonly SpawnPattern[] {
  return patterns.filter((p) => difficulties.includes(p.difficulty));
}

export class SpawnSystem {
  private lastPatternId: string | null = null;

  constructor(
    private scene: Phaser.Scene,
    private rng: SeededRNG,
    private factories: SpawnFactories,
  ) {}

  tick(difficulties: readonly Difficulty[], scrollSpeed: number): SpawnPattern | undefined {
    const pool = poolForPhase(PATTERNS, difficulties);
    if (pool.length === 0) return undefined;
    const pattern = this.rng.pick(pool);
    this.lastPatternId = pattern.id;
    this.runPattern(pattern, scrollSpeed);
    return pattern;
  }

  getLastPatternId(): string | null {
    return this.lastPatternId;
  }

  private runPattern(pattern: SpawnPattern, scrollSpeed: number): void {
    // Mirror lane 0↔2 (50%): pattern bên trái thành bên phải, voi gặp shape mới
    const mirror = this.rng.next() < 0.5;
    // Jitter delay ±15% để timing không lặp cứng
    const jitterFactor = 0.85 + this.rng.next() * 0.3;
    // Skip 1 entity ngẫu nhiên (25% chance cho pattern 4+ entity)
    const skipIdx =
      pattern.entities.length >= 4 && this.rng.next() < 0.25
        ? this.rng.rangeInt(0, pattern.entities.length - 1)
        : -1;

    pattern.entities.forEach((entity, idx) => {
      if (idx === skipIdx) return;
      const lane = (mirror ? 2 - entity.lane : entity.lane) as Lane;
      const transformed: PatternEntity = { ...entity, lane };
      const delay = Math.round(entity.delay * jitterFactor);
      if (delay === 0) {
        this.spawnEntity(transformed, scrollSpeed);
      } else {
        this.scene.time.delayedCall(delay, () => {
          this.spawnEntity(transformed, scrollSpeed);
        });
      }
    });
  }

  private spawnEntity(entity: PatternEntity, scrollSpeed: number): void {
    if (entity.type === 'nutrient') {
      const key = (entity.subtype as NutrientKey | undefined) ??
        this.rng.weightedPick(weightedEntries());
      const nutrient = this.factories.acquireNutrient();
      nutrient.spawn(key, entity.lane as Lane, scrollSpeed);
      nutrient.setDepth(10);
    } else if (entity.type === 'monster') {
      const monster = this.factories.acquireMonster();
      monster.spawn(entity.lane as Lane, scrollSpeed);
      monster.setDepth(10);
    }
    // shield không nằm trong pattern (timer-based, GameScene xử lý riêng)
  }
}
